import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PhotoTile from '../components/PhotoTile';
import OfflinePill from '../components/OfflinePill';
import { SkeletonGrid } from '../components/Skeleton';
import { IconPlay, MarkBall, MarkBoot, MarkTrophy, MarkWhistle } from '../components/Icon';
import {
  hasMoreMedia, loadMoreMedia, mediaLoaded, mediaLoadError, retryMediaLoad, useLiked, useMediaPolling, useMedia,
  visibleTo,
} from '../lib/media';
import { NotSignedIn } from '../lib/api';
import { RequestTimeout } from '../lib/fetch-timeout';
import { useSession } from '../lib/session';
import { inTab, isTab, type Tab } from '../lib/gallery-tabs';
import { useSchedule } from '../lib/content';
import { dayKey, now } from '../lib/time';

/**
 * Featured first, and it is the default (D51/§7).
 *
 * `hidden` is gone (D50). **The order mattered:** `/photos/{id}/visibility` was
 * deleted and the gallery read moved to the `all` partition in one breath,
 * because a hide returning `ok` against a gallery already reading `all` reports
 * success and moves nothing.
 *
 * `liked` is a client-side filter, not a fourth server read — `likedIds` is
 * complete and fetched once at boot. It is the one tab that can be incomplete: a
 * photo liked long ago that has fallen out of the loaded window is in the set
 * but not the store, and appears as the scroll reaches it.
 */
/** The tab set and its predicates live in `gallery-tabs`, because photo detail
 *  walks the same list and the two must not drift. */
type Filter = Tab;

const PAGE = 24;

/**
 * Artboard 1h — the social surface of the site. Every guest sees every upload
 * (D35). One filter rather than separate pages; square tiles so six hundred scan
 * quickly; the uploader's name and suit mark on a card foot under the image.
 */
export default function Gallery() {
  const session = useSession();
  const media = useMedia();
  const liked = useLiked();
  // Other guests' uploads arrive by poll, not by push (D12/D17).
  useMediaPolling(20_000);
  const schedule = useSchedule();
  /**
   * The tab lives in the URL, not in component state, and that is a bug fix
   * rather than a preference.
   *
   * `useState` here is local to a mounted `Gallery`. Opening a photo unmounts
   * it and coming back mounts a fresh one at the default, so every trip through
   * photo detail silently returned a guest to Featured — as did every refresh.
   * The same bug existed before Featured arrived and was invisible, because the
   * default was `all` and resetting to it looked like it had worked.
   *
   * `replace` rather than push: tapping through four tabs should not put four
   * entries in the history for the back button to walk out through.
   */
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const filter: Filter = isTab(raw) ? raw : 'featured';
  const setFilter = (next: Filter) => {
    setParams(next === 'featured' ? {} : { tab: next }, { replace: true });
  };
  const [count, setCount] = useState(PAGE);
  const [fetching, setFetching] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => visibleTo(media), [media]);

  const filtered = useMemo(
    () => inTab(visible, filter, session, liked),
    [visible, filter, session, liked],
  );

  /**
   * Two paginations, one sentinel, and they are not the same thing. `count` is
   * how many rows already in the browser get rendered, purely so a thousand
   * tiles do not mount at once; `loadMoreMedia` behind it fetches the next page,
   * which is what decides whether a photo can be reached at all.
   *
   * With only the first, over a fixed `?limit=100`, the scroll worked perfectly
   * and simply ended after a hundred photos — no error, no empty state,
   * indistinguishable from the end of the week. Two hundred guests over four
   * days cross that on the first night.
   */
  useEffect(() => { setCount(PAGE); }, [filter]);
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      setCount((n) => n + PAGE);
      // Ask before the rendered window runs out, so the next page is usually
      // here by the time it is scrolled to. On a resort connection the round
      // trip is the part that shows.
      if (count + PAGE * 2 >= filtered.length && hasMoreMedia()) {
        setFetching(true);
        loadMoreMedia()
          .catch(() => { /* Offline. What is loaded stays; the poll retries. */ })
          .finally(() => setFetching(false));
      }
    }, { rootMargin: '600px' });
    io.observe(node);
    return () => io.disconnect();
  }, [filtered.length, count]);

  const page = filtered.slice(0, count);
  const eventName = (id?: string) => schedule.data?.events.find((e) => e.id === id)?.name;

  // Today's uploads sit under the event they came from; older ones say their
  // day.
  const heading = (() => {
    const first = page[0];
    if (!first) return null;
    if (dayKey(first.createdAt) === dayKey(now())) return eventName(first.eventId) ?? 'Today';
    return eventName(first.eventId) ?? 'Earlier this week';
  })();

  // "No photos yet" and "not asked yet" look identical from the item count
  // alone, and the empty state tells a guest still loading that their photos
  // are gone.
  //
  // Unless the load failed. Then the skeleton would be a lie that never ends,
  // which is what it was: a failed or hung first read showed it for as long as
  // the tab was open. The poll keeps retrying underneath either way.
  if (!mediaLoaded() && media.length === 0) {
    const failed = mediaLoadError();
    return <div className="page">{failed ? <LoadFailed error={failed} /> : <SkeletonGrid />}</div>;
  }

  return (
    <div className="page">
      <OfflinePill />

      <div className={`gal-bar${session?.isAdmin ? ' is-admin' : ''}`}>
        <div className="seg" style={{ fontSize: 12 }}>
          {/* All three are for everyone now. Featured is a tab, not a shelf
              (D51) — the admin branch that used to guard the third one is the
              thing that goes, not the third one itself. */}
          <FilterOption value="featured" filter={filter} setFilter={setFilter} label="Featured" />
          <FilterOption value="all" filter={filter} setFilter={setFilter} label="All" />
          <FilterOption value="mine" filter={filter} setFilter={setFilter} label="Mine" />
          <FilterOption value="liked" filter={filter} setFilter={setFilter} label="Liked" />
        </div>
        <div style={{ flex: 1 }} />
        {session?.isAdmin && (
          /* With the photos rather than in Profile: it is a thing you do to
             this collection, and whoever starts it on the reception laptop is
             already looking at the gallery. */
          <Link className="btn btn-secondary" style={{ flex: 'none', gap: 6, minHeight: 36 }} to="/display">
            <IconPlay size={13} /> Slideshow
          </Link>
        )}
        {/* This counts what has been *downloaded*, not what was uploaded, and
            with paging that climbs as you scroll — hence `249+` rather than a
            total it does not know. The exact number needs the `visiblePhotos`
            counter the API keeps for the Now screen, unread here. */}
        <span className="gal-count">
          {visible.length}{hasMoreMedia() ? '+' : ''} photos so far
        </span>
      </div>

      {filtered.length === 0 ? (
        <EmptyFor filter={filter} setFilter={setFilter} />
      ) : (
        <>
          {heading && (
            <div className="gal-head">
              <h4>{heading}</h4>
              <div className="gal-count">
                {filter === 'mine' && `${filtered.length} of yours`}
                {filter === 'featured' && `${filtered.length} on the big screen`}
                {filter === 'liked' && `${filtered.length} you liked`}
                {filter === 'all' && `${filtered.length} to look through`}
              </div>
            </div>
          )}
          <div className="gal-grid">
            {/* The tab rides along, so the viewer walks this tab and back
                returns to it. */}
            {page.map((item) => (
              <PhotoTile key={item.id} item={item} tab={filter} showState={filter === 'mine'} />
            ))}
          </div>
          <div ref={sentinel} style={{ height: 1 }} />
          {(count < filtered.length || fetching) && <SkeletonGrid tiles={2} />}
        </>
      )}
    </div>
  );
}

function FilterOption({ value, filter, setFilter, label }: {
  value: Filter; filter: Filter; setFilter: (f: Filter) => void; label: string;
}) {
  return (
    <label className="seg-opt">
      <input type="radio" name="gallery-filter" checked={filter === value} onChange={() => setFilter(value)} />
      {label}
    </label>
  );
}

/**
 * The first load failed. Says what kind of failure in words a guest can act
 * on, and offers the retry the poll would make anyway, now rather than in
 * twenty seconds.
 */
export function LoadFailed({ error }: { error: unknown }) {
  if (error instanceof NotSignedIn) {
    return (
      <div className="empty">
        <MarkBall className="mark" size={44} />
        <h4 style={{ margin: 0 }}>Your sign-in ran out</h4>
        <p role="alert">Sign in again and the photos will be right here.</p>
        <Link className="btn btn-primary" to="/">Sign in</Link>
      </div>
    );
  }
  const slow = error instanceof RequestTimeout;
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  return (
    <div className="empty">
      {/* The whistle and the wording match the Schedule's own failed load. */}
      <MarkWhistle className="mark" size={44} />
      <h4 style={{ margin: 0 }}>The photos didn't load</h4>
      {/* The alert is the sentence, not the panel: a screen reader reads out
          what happened, and the button is reached the usual way. */}
      <p role="alert">
        {offline
          ? "You're offline. They'll appear when the wifi is back."
          : slow
            ? 'The connection is slow and the photos took too long to arrive.'
            : 'Something went wrong getting the photos.'}
        {' '}We'll keep trying.
      </p>
      <button type="button" className="btn btn-primary" onClick={() => { void retryMediaLoad(); }}>
        Try again
      </button>
    </div>
  );
}

function EmptyFor({ filter, setFilter }: { filter: Filter; setFilter: (f: Filter) => void }) {
  /* An invitation rather than a dead end, because this state is genuinely
     reachable: nothing features on arrival, not even an organiser's own uploads
     (D52), so on the first evening this is the default tab AND the whole
     projector, empty until somebody seeds it. Empty by accident and empty on day
     one look identical to a guest. */
  if (filter === 'featured') {
    return (
      <div className="empty">
        <MarkTrophy className="mark" size={44} />
        <h4 style={{ margin: 0 }}>Nothing on the big screen yet</h4>
        <p>Photos land here when three people like them. Have a look through All and start somebody off.</p>
        <button type="button" className="btn btn-primary" onClick={() => setFilter('all')}>See everything</button>
      </div>
    );
  }
  if (filter === 'liked') {
    return (
      <div className="empty">
        <MarkBall className="mark" size={44} />
        <h4 style={{ margin: 0 }}>You have not liked anything yet</h4>
        <p>Tap the heart on any photo and it collects here. Three likes puts one on the big screen.</p>
        <button type="button" className="btn btn-primary" onClick={() => setFilter('all')}>See everything</button>
      </div>
    );
  }
  if (filter === 'mine') {
    // Invite rather than apologise; All will not be empty for long.
    return (
      <div className="empty">
        <MarkBoot className="mark" size={44} />
        <h4 style={{ margin: 0 }}>Nothing of yours yet</h4>
        <p>Whatever you take this week ends up here, with your name on it — and on the big screen at the reception.</p>
        <Link className="btn btn-primary" to="/upload">Add the first one</Link>
      </div>
    );
  }
  return (
    <div className="empty">
      <MarkBall className="mark" size={44} />
      <h4 style={{ margin: 0 }}>No photos yet</h4>
      <p>Somebody has to go first. It may as well be you.</p>
      <Link className="btn btn-primary" to="/upload">Add a photo</Link>
    </div>
  );
}
